// ============================================================================
// Encounter API module.
//
// Post-2026-04 refactor: the backend now returns the richer UI-shaped payload
// natively — Vitals carry bp_systolic/bp_diastolic, orders carry items[], and
// a real Addendum table exists. Most of the old adapter code is gone; what
// remains is the small mapping between the ICD-10 read shape and the flat
// `icd10_codes` list the legacy clinical-note editor still consumes.
// ============================================================================
//
// Remaining UI↔backend bridges (intentional, not a shape mismatch):
//   * ICD-10 read objects have {code, title}; UI uses {code, description}.
//     `icd10Out` aliases title → description.
//   * The UI still treats Encounter.icd10_codes as a flat list rather than a
//     Diagnosis[] with diagnosis_type. `syncIcd10Diagnoses` diffs the list
//     and issues POST/DELETE for the diffs.
// ============================================================================

import { api } from './client.js'
import { endpoints } from './endpoints.js'
import { unwrapList } from '../utils/format.js'

// ---------------------------------------------------------------------------
// Shape adapters
// ---------------------------------------------------------------------------

// Real ICD10: { id, code, title, category, label } → UI: { id, code, description }
function icd10Out(real) {
  if (!real) return null
  return {
    id: real.id,
    code: real.code,
    description: real.title || real.label || '',
    title: real.title,
    category: real.category || '',
  }
}

// Real Diagnosis: { id, encounter, diagnosis_type, icd10:{...}, diagnosis_text }
// UI treats the encounter's diagnoses as a flat ICD-10 list. We surface the
// ICD10 row with a `_diagnosisId` back-reference so we can DELETE the right row.
function diagnosesToIcd10Codes(diagnoses = []) {
  return diagnoses
    .filter((d) => d.icd10)
    .map((d) => ({
      ...icd10Out(d.icd10),
      _diagnosisId: d.id,
      _diagnosisType: d.diagnosis_type,
      // Doctor's free-text clinical narrative — supplements the ICD-10
      // code and renders before the code in parens on generated
      // certificates. Optional; '' when the chip hasn't been annotated.
      _narrative: d.clinical_narrative || '',
    }))
}

// Backend VitalSign row → UI shape the VitalsForm expects.
// Post-refactor the backend returns bp_systolic/bp_diastolic directly; we
// only remap the field names (temperature → temperature_c etc.) to match the
// UI's clinical convention.
function latestVitals(vital_signs = []) {
  if (!vital_signs.length) return {}
  // Pick the row with the latest recorded_at (fall back to id order).
  const sorted = [...vital_signs].sort((a, b) =>
    (b.recorded_at || '').localeCompare(a.recorded_at || ''))
  const v = sorted[0]
  return {
    _vitalId: v.id,
    temperature_c: v.temperature,
    heart_rate: v.pulse_rate,
    resp_rate: v.respiratory_rate,
    bp_systolic: v.bp_systolic,
    bp_diastolic: v.bp_diastolic,
    spo2: v.oxygen_saturation,
    weight_kg: v.weight_kg,
    height_cm: v.height_cm,
    recorded_at: v.recorded_at,
  }
}

// Shape a real Encounter into the flat shape the existing UI expects.
// Orders now come back from the backend with items[] natively, so we pass
// them through unchanged — no wrapper needed.
export function adaptEncounter(real) {
  if (!real) return real
  const note = real.clinical_note || null
  return {
    ...real,
    // Flattened fields for the clinical-note editor
    narrative: note?.history_of_presenting_illness || '',
    structured: {
      vitals: latestVitals(real.vital_signs),
      clinical_note: note,          // full note available here for later use
    },
    icd10_codes: diagnosesToIcd10Codes(real.diagnoses),
    addendums: real.addendums || [],
    submitted_at: note?.submitted_at || null,

    // Orders pass through — backend already returns items[], new field names,
    // and matches what the UI renders.
    prescriptions:       real.prescriptions       || [],
    lab_orders:          real.lab_orders          || [],
    imaging_orders:      real.imaging_orders      || [],
    ambulance_requests:  real.ambulance_requests  || [],
    referrals:           real.referrals           || [],
  }
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export async function listEncounters(params = {}) {
  const { data } = await api.get(endpoints.encounterList, { params })
  return unwrapList(data).map(adaptEncounter)
}

export async function getEncounter(id) {
  const { data } = await api.get(endpoints.encounterDetail(id))
  return adaptEncounter(data)
}

// ---------------------------------------------------------------------------
// Create / update encounter-level fields
// ---------------------------------------------------------------------------

export async function createEncounter(patientId, extra = {}) {
  const body = { patient: patientId, status: 'draft', ...extra }
  const { data } = await api.post(endpoints.encounterCreate, body)
  // The create response is the write serializer (no nested). Re-fetch
  // detail so the caller gets the full hydrated+adapted shape.
  return getEncounter(data.id)
}

/**
 * updateEncounter accepts the legacy flat patch:
 *   { narrative, structured: { vitals }, icd10_codes, chief_complaint, status }
 * and fans it out across the real Django tables (ClinicalNote, VitalSign,
 * Diagnosis, Encounter) as needed, then returns the re-fetched detail.
 */
export async function updateEncounter(id, patch) {
  // 1) Encounter-level fields (chief_complaint, status, consultation_type)
  const encFields = {}
  if (patch.chief_complaint !== undefined) encFields.chief_complaint = patch.chief_complaint
  if (patch.consultation_type !== undefined) encFields.consultation_type = patch.consultation_type
  if (patch.status !== undefined) encFields.status = patch.status
  if (Object.keys(encFields).length) {
    await api.patch(endpoints.encounterUpdate(id), encFields)
  }

  // 2) Narrative → ClinicalNote.history_of_presenting_illness (upsert)
  if (patch.narrative !== undefined) {
    await upsertClinicalNote(id, {
      history_of_presenting_illness: patch.narrative,
    })
  }

  // 3) Structured patch may contain { vitals, clinical_note: {...} }
  if (patch.structured) {
    if (patch.structured.vitals !== undefined) {
      await upsertVitals(id, patch.structured.vitals)
    }
    if (patch.structured.clinical_note) {
      await upsertClinicalNote(id, patch.structured.clinical_note)
    }
  }

  // 4) Full ICD-10 list replacement → diff against current diagnoses
  if (Array.isArray(patch.icd10_codes)) {
    await syncIcd10Diagnoses(id, patch.icd10_codes)
  }

  return getEncounter(id)
}

/**
 * Upsert this encounter's ClinicalNote.
 *
 * IMPORTANT: this used to GET /api/clinical-notes/clinical-notes/?encounter=N
 * but django-filter is NOT installed, so `filterset_fields` is silently
 * ignored — the GET returned every clinical note the doctor could see and
 * `[0]` could land on the wrong row, causing PATCHes that updated some
 * other encounter's note. The FE then showed "Saved" because the PATCH
 * itself succeeded, but encounter N's note stayed empty.
 *
 * Fix: go through the encounter detail endpoint, which has the
 * OneToOne-related clinical_note inlined. That's unambiguous — there is
 * exactly one (or zero) clinical_note per encounter, and we get its id
 * directly from the row we're editing.
 *
 * We also harden against the "two parallel saves both POST a new note"
 * race: if the POST fails, re-fetch the encounter and PATCH the note
 * the other save just created.
 */
async function upsertClinicalNote(encounterId, fields) {
  const { data: enc } = await api.get(endpoints.encounterDetail(encounterId))
  const existing = enc?.clinical_note
  if (existing && existing.id) {
    const { data } = await api.patch(endpoints.clinicalNoteDetail(existing.id), fields)
    return data
  }
  try {
    const { data } = await api.post(endpoints.clinicalNoteCreate, {
      encounter: encounterId,
      ...fields,
    })
    return data
  } catch (err) {
    // Concurrency safety net: another save just created the note. Re-fetch
    // the encounter and PATCH the note that exists now.
    const { data: enc2 } = await api.get(endpoints.encounterDetail(encounterId))
    if (enc2?.clinical_note?.id) {
      const { data } = await api.patch(
        endpoints.clinicalNoteDetail(enc2.clinical_note.id),
        fields,
      )
      return data
    }
    throw err
  }
}

// UI vitals shape → backend VitalSign row. Backend now accepts bp_systolic /
// bp_diastolic directly; the serializer syncs the legacy blood_pressure
// string server-side. We remap clinical field names only.
function vitalsToBackend(vitals) {
  if (!vitals || typeof vitals !== 'object') return {}
  const out = {}
  const put = (k, v) => {
    if (v === undefined || v === null || v === '') return
    out[k] = v
  }
  put('temperature',       vitals.temperature       ?? vitals.temperature_c)
  put('pulse_rate',        vitals.pulse_rate        ?? vitals.heart_rate)
  put('respiratory_rate',  vitals.respiratory_rate  ?? vitals.resp_rate)
  put('oxygen_saturation', vitals.oxygen_saturation ?? vitals.spo2)
  put('weight_kg',         vitals.weight_kg)
  put('height_cm',         vitals.height_cm)
  put('bp_systolic',       vitals.bp_systolic)
  put('bp_diastolic',      vitals.bp_diastolic)
  return out
}

async function upsertVitals(encounterId, vitals) {
  // Strategy: always append a new VitalSign row (an audit-friendly log).
  // The UI reads the latest row; creating a new one preserves history.
  const mapped = vitalsToBackend(vitals)
  if (Object.keys(mapped).length === 0) return
  const body = { encounter: encounterId, ...mapped }
  await api.post(endpoints.vitalSignCreate, body)
}

async function syncIcd10Diagnoses(encounterId, nextCodes) {
  // Same django-filter issue as upsertClinicalNote — listing diagnoses with
  // ?encounter=N silently returns all of them. Use the encounter detail's
  // nested `diagnoses` array as the unambiguous source of "what's
  // currently attached".
  const { data: enc } = await api.get(endpoints.encounterDetail(encounterId))
  const currentDx = enc?.diagnoses || []

  // Diff is over (icd10_id, diagnosis_type) pairs — the same code can
  // legitimately appear in multiple type buckets (provisional today,
  // also part of the differential list).
  const key = (codeId, type) => `${codeId}::${type || 'provisional'}`
  const currentKeys = new Set(currentDx.map((d) => key(d.icd10?.id, d.diagnosis_type)))
  const nextKeys    = new Set(nextCodes.map((c) => key(c.id, c._diagnosisType)))

  const toDelete = currentDx.filter((d) => d.icd10 && !nextKeys.has(key(d.icd10.id, d.diagnosis_type)))
  const toAdd    = nextCodes.filter((c) => c.id && !currentKeys.has(key(c.id, c._diagnosisType)))

  await Promise.all(
    toDelete.map((d) => api.delete(endpoints.diagnosisDetail(d.id)))
  )
  await Promise.all(
    toAdd.map((c) => api.post(endpoints.diagnosisCreate, {
      encounter: encounterId,
      diagnosis_type: c._diagnosisType || 'provisional',
      icd10_id: c.id,
      diagnosis_text: c.description || c.title || c.code,
      // Carry narrative through on initial create. Almost always blank
      // here (chip is being added fresh) but supported so callers can
      // pre-populate if they ever need to.
      clinical_narrative: c._narrative || '',
    }))
  )
}

/**
 * PATCH a single diagnosis row. Used by the per-chip narrative textarea
 * in the J / K / M sections of EncounterEditor.
 *
 * The narrative is the doctor's free-text supplement to the ICD-10 code.
 * On the printed certificate it renders BEFORE the code in parentheses
 * so the document reads like a clinical letter rather than a structured
 * data dump.
 *
 *     patchDiagnosis(42, { clinical_narrative: 'well-controlled on amlodipine' })
 *       → PATCH /api/clinical-notes/diagnoses/42/
 *         body: { clinical_narrative: 'well-controlled on amlodipine' }
 *
 * Returns the server's representation of the updated row so the caller
 * can re-sync local state if needed.
 */
export async function patchDiagnosis(diagnosisId, partial) {
  const { data } = await api.patch(
    endpoints.diagnosisDetail(diagnosisId),
    partial,
  )
  return data
}

/**
 * Type-scoped diagnosis sync for the structured editor.
 *
 * Replaces the current set of diagnoses of ONE diagnosis_type
 * (provisional / differential / final) without disturbing the others.
 * Used by EncounterEditor.jsx where each section owns its own bucket.
 *
 * Usage:
 *   await setDiagnosesOfType(encId, 'provisional', [icd10code]) // 0 or 1
 *   await setDiagnosesOfType(encId, 'differential', [c1, c2, …]) // any number
 */
/**
 * Re-route a prescription to a different pharmacy. The original Rx is
 * preserved (history); a new Prescription is created with the same
 * items addressed to the new pharmacy. Returns the new prescription.
 *
 * Doctor's main use case: a pharmacy marks an item "Not Available" and
 * the doctor wants to redirect the script to a different one without
 * losing the audit trail of the first attempt.
 */
export async function reroutePrescription(prescriptionId, { pharmacy_id, reason } = {}) {
  const { data } = await api.post(endpoints.prescriptionReroute(prescriptionId), {
    pharmacy_id, reason: reason || '',
  })
  return data
}

export async function setDiagnosesOfType(encounterId, diagnosisType, codes) {
  const { data: enc } = await api.get(endpoints.encounterDetail(encounterId))
  const currentDx = (enc?.diagnoses || []).filter((d) => d.diagnosis_type === diagnosisType)
  const nextIds = new Set((codes || []).map((c) => c.id).filter(Boolean))
  const currentIds = new Set(currentDx.map((d) => d.icd10?.id).filter(Boolean))

  const toDelete = currentDx.filter((d) => d.icd10 && !nextIds.has(d.icd10.id))
  const toAdd    = (codes || []).filter((c) => c.id && !currentIds.has(c.id))

  await Promise.all(
    toDelete.map((d) => api.delete(endpoints.diagnosisDetail(d.id)))
  )
  await Promise.all(
    toAdd.map((c) => api.post(endpoints.diagnosisCreate, {
      encounter: encounterId,
      diagnosis_type: diagnosisType,
      icd10_id: c.id,
      diagnosis_text: c.description || c.title || c.code,
    }))
  )
}

// ---------------------------------------------------------------------------
// Submit / reopen
// ---------------------------------------------------------------------------

export async function submitEncounter(id) {
  const { data } = await api.post(endpoints.encounterSubmit(id))
  return adaptEncounter(data)
}

export async function reopenEncounter(id) {
  const { data } = await api.post(endpoints.encounterReopen(id))
  return adaptEncounter(data)
}

/**
 * Post-finalize addendum. Backend auto-fills author from the authenticated
 * DoctorProfile, so the frontend just sends encounter + text.
 */
export async function addAddendum(encounterId, text) {
  const body = { encounter: encounterId, text: (text || '').trim() }
  if (!body.text) throw new Error('Addendum text is required.')
  const { data } = await api.post(endpoints.addendumCreate, body)
  return data
}

// ---------------------------------------------------------------------------
// Orders attached to an encounter.
//
// Post-refactor, the backend accepts items[] directly on create. One POST
// per order header; the server handles the line rows.
// ---------------------------------------------------------------------------

/**
 * createPrescription(encId, { pharmacy, notes, items: [...] })
 * items[]: { drug_code, drug_name, dose, frequency, duration, quantity, instructions }
 */
export async function createPrescription(encId, body = {}) {
  const items = Array.isArray(body.items) && body.items.length
    ? body.items
    : [{
        drug_name: body.medication_name || '',
        dose: body.dosage || '',
        frequency: body.frequency || '',
        duration: body.duration || '',
      }]
  const row = {
    encounter: encId,
    items,
    notes: body.notes || '',
  }
  const pharmacy = body.pharmacy ?? body.target_pharmacy_id ?? null
  if (pharmacy) row.pharmacy = pharmacy
  const { data } = await api.post(endpoints.prescriptionCreate, row)
  return data
}

/**
 * createLabOrder(encId, { laboratory, clinical_notes, items: [...] })
 * items[]: { test_code, test_name, loinc_code, urgency }
 */
export async function createLabOrder(encId, body = {}) {
  const items = Array.isArray(body.items) && body.items.length
    ? body.items
    : [{ test_name: body.test_name || '' }]
  const row = {
    encounter: encId,
    items,
    clinical_notes: body.clinical_notes || '',
  }
  const laboratory = body.laboratory ?? body.target_lab_id ?? null
  if (laboratory) row.laboratory = laboratory
  const { data } = await api.post(endpoints.labOrderCreate, row)
  return data
}

/**
 * createImagingOrder(encId, { radiology_center, clinical_indication, items })
 * items[]: { study_code, study_name, modality, urgency }
 */
export async function createImagingOrder(encId, body = {}) {
  const items = Array.isArray(body.items) && body.items.length
    ? body.items
    : [{ study_name: body.imaging_type || '' }]
  const row = {
    encounter: encId,
    items,
    clinical_indication: body.clinical_indication || '',
  }
  const center = body.radiology_center ?? body.target_imaging_id ?? null
  if (center) row.radiology_center = center
  const { data } = await api.post(endpoints.imagingOrderCreate, row)
  return data
}

/**
 * createReferral(encId, { to_facility, to_doctor, specialty, urgency, reason })
 *
 * Accepts either `to_facility` (HealthFacility FK id) or the legacy
 * `target_organization_id` alias the ReferralForm historically sent.
 * The Referral.from_doctor FK is auto-filled server-side from request.user's
 * DoctorProfile.
 */
export async function createReferral(encId, body = {}) {
  const row = {
    encounter: encId,
    reason: body.reason || '',
    specialty: body.specialty || '',
    urgency: body.urgency || 'routine',
    // Structured sending / receiving metadata — added in the 2026-04-23
    // referral-form refactor. Backend accepts blank strings; we send '' for
    // any field the user left empty.
    from_facility_type: body.from_facility_type || '',
    from_cadre: body.from_cadre || '',
    from_address: body.from_address || '',
    from_specialty: body.from_specialty || '',
    to_facility_type: body.to_facility_type || '',
    to_cadre: body.to_cadre || '',
    // Free-text receiving-doctor name typed in the form ("Dr Sarah Maino").
    // The printed letter uses this for "Dear Dr Maino,"; backend coerces
    // null/undefined to "" via ReferralSerializer.validate_to_name. Empty
    // string here means the letter renders "Dear colleague,".
    to_name: body.to_name || '',
  }
  if (body.to_doctor) row.to_doctor = body.to_doctor
  const facility = body.to_facility ?? body.target_organization_id ?? null
  if (facility) row.to_facility = facility
  const { data } = await api.post(endpoints.referralCreate, row)
  return data
}

/**
 * createAmbulance(encId, { service_type, pickup_location, destination,
 *                          urgency, clinical_summary, ambulance_service })
 * All the UI-side fields are modeled on the backend now.
 */
export async function createAmbulance(encId, body = {}) {
  const row = {
    encounter: encId,
    // service_type is "what kind of transport" (Routine transport,
    // ICU transfer, Dialysis run, ...); urgency is "how fast"
    // (routine / priority / emergency). They are independent — do
    // NOT fall back to urgency. Earlier this helper had
    // `body.urgency` in the chain, which produced lowercase rows
    // like service_type='emergency' that read awkwardly next to
    // the urgency chip and duplicated information on the queue.
    service_type: body.service_type || 'Routine transport',
    pickup_location: body.pickup_location || body.pickup_address || '',
    destination: body.destination || '',
    urgency: body.urgency || 'routine',
    clinical_summary: body.clinical_summary || '',
  }
  const svc = body.ambulance_service ?? body.target_provider_id ?? null
  if (svc) row.ambulance_service = svc
  if (body.catalogue_item) row.catalogue_item = body.catalogue_item
  const { data } = await api.post(endpoints.ambulanceCreate, row)
  return data
}
